import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import { Label } from "@/common/components/ui/label";
import { Input } from "@/common/components/ui/input";
import { Button } from "@/common/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/common/components/ui/table";
import {
  User,
  MapPin,
  CreditCard,
  Package,
  IndianRupee,
  Truck,
  ArrowLeft,
  Upload,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

import GoldService from "@/services/FinEaseHomeAndAdmin/APIs/GoldService";
import { notify } from "@/common/components/common/Notification";

const OrderDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [shippingForm, setShippingForm] = useState({
    courier: "",
    tracking: "",
    status: "",
  });
  const [mmtcOrderIdDraft, setMmtcOrderIdDraft] = useState("");
  const [isSavingMmtcId, setIsSavingMmtcId] = useState(false);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!id) {
        return;
      }
      try {
        setIsLoading(true);
        const res = await GoldService.getOrderById(id);
        const data = res?.data ?? res;

        if (data && (data.id || data.order_id)) {
          setSelectedOrder(data);
          setShippingForm({
            courier: data.courier_partner ?? "",
            tracking: data.tracking_number ?? "",
            status: data.order_status ?? "",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: "Order not found",
          });
          navigate("/admin/physical-gold/orders");
        }
      } catch (_err) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch order details",
        });
        navigate("/admin/physical-gold/orders");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id, navigate, toast]);

  useEffect(() => {
    if (selectedOrder) {
      setMmtcOrderIdDraft(String(selectedOrder.mmtcid ?? "").trim());
    }
  }, [selectedOrder]);

  const savedMmtcOrderId = String(selectedOrder?.mmtcid ?? "").trim();
  const isMmtcOrderIdDirty = mmtcOrderIdDraft.trim() !== savedMmtcOrderId;

  const handleSaveMmtcOrderId = async () => {
    if (!id) {
      return;
    }
    const trimmed = mmtcOrderIdDraft.trim();
    setIsSavingMmtcId(true);
    try {
      const res = await GoldService.updateOrder(id, { mmtcid: trimmed });
      if (res?.success || res) {
        notify.success(res?.message);
        navigate("/admin/physical-gold/orders");
      }
    } catch (err: any) {
      notify.error(err?.message);
    } finally {
      setIsSavingMmtcId(false);
    }
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedOrder || !id) {
      return;
    }

    const payload: any = {
      order_status: status,
    };

    if (status === "DISPATCHED") {
      if (!shippingForm.courier || !shippingForm.tracking) {
        notify.error("Courier and Tracking details are required for Dispatch.");
        return;
      }
      payload.courier_partner = shippingForm.courier;
      payload.tracking_number = shippingForm.tracking;
    } else {
      // Allow sending nulls if needed, or just status
      payload.courier_partner = shippingForm.courier || null;
      payload.tracking_number = shippingForm.tracking || null;
    }

    try {
      setIsLoading(true);
      const res = await GoldService.updateOrder(id, payload);
      if (res?.success || res) {
        notify.success(res?.message);
        navigate("/admin/physical-gold/orders");
      }
    } catch (err: any) {
      notify.error(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const normalized = status ?? "";
    if (normalized.includes("new_order")) {
      return "bg-blue-100 text-blue-700";
    }
    if (normalized.includes("order_confirmed")) {
      return "bg-cyan-100 text-cyan-700";
    }
    if (normalized.includes("order_processing")) {
      return "bg-amber-100 text-amber-700";
    }
    if (normalized.includes("order_dispatched")) {
      return "bg-purple-100 text-purple-700";
    }
    if (normalized.includes("on_the_way")) {
      return "bg-indigo-100 text-indigo-700";
    }
    if (normalized.includes("order_delivered")) {
      return "bg-green-100 text-green-700";
    }
    if (normalized.includes("order_return")) {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized.includes("order_cancelled")) {
      return "bg-red-100 text-red-700";
    }
    return "bg-gray-100 text-gray-700";
  };

  const handleInvoiceUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !id) {
      return;
    }

    if (file.type !== "application/pdf") {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload a PDF file for the invoice.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("invoice", file);

    try {
      setIsUploadingInvoice(true);
      const res = await GoldService.uploadInvoice(id, formData);
      if (res?.success || res?.status === "success" || res) {
        toast({
          title: "Success",
          description: "Invoice uploaded successfully.",
        });
        // Optionally fetch order details again to update UI
      }
    } catch (_err) {
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description:
          "There was an error uploading the invoice. Please try again.",
      });
    } finally {
      setIsUploadingInvoice(false);
      // Reset input
      event.target.value = "";
    }
  };

  if (isLoading || !selectedOrder) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/50">
        <div className="text-slate-500">Loading order details...</div>
      </div>
    );
  }

  const normalizedStatusStr = selectedOrder.order_status ?? "new_order";
  const displayStatus = normalizedStatusStr.replace("_", " ");

  // Safely get delivery address representation
  const address =
    selectedOrder.delivery_address_detail ?? selectedOrder.delivery_address;
  const isAddressObj = typeof address === "object" && address !== null;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto w-full max-w-7xl">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-5">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate("/admin/physical-gold/orders")}
                className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" />
              </Button>
              <div>
                <h1 className="font-sans text-lg font-bold tracking-tight text-slate-900">
                  Order{" "}
                  {selectedOrder.order_id
                    ? `${selectedOrder.order_id}`
                    : `#${selectedOrder.id?.substring(0, 8)}`}
                </h1>
                <p className="text-sm font-medium text-slate-500">
                  MMTC Ref: {selectedOrder.mmtcid ?? "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge
                variant="outline"
                className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${getStatusColor(
                  normalizedStatusStr
                )}`}
              >
                {displayStatus}
              </Badge>
              <div className="relative">
                <Input
                  type="file"
                  accept=".pdf"
                  className="absolute inset-0 z-50 h-full w-full cursor-pointer opacity-0 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                  onChange={handleInvoiceUpload}
                  disabled={isUploadingInvoice}
                  title="Upload Invoice"
                />
                <Button
                  variant="outline"
                  className="cursor-pointer gap-2 border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50"
                  disabled={isUploadingInvoice}
                >
                  {isUploadingInvoice ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {isUploadingInvoice ? "Uploading..." : "Upload Invoice"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl space-y-6 p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Customer Information */}
          <Card className="flex flex-col rounded-xl border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                <User className="h-4 w-4" /> Customer Info
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 space-y-5 pt-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Customer ID
                  </Label>
                  <p className="mt-1 break-all font-mono text-xs font-bold text-slate-700">
                    {selectedOrder.customer_detail?.id ??
                      selectedOrder.customer ??
                      "-"}
                  </p>
                </div>

                <div className="space-y-2">
                  <div>
                    <Label className="text-[10px] font-bold uppercase text-slate-400">
                      Order ID (MMTC)
                    </Label>
                    <p className="mt-0.5 text-[10px] text-slate-400">
                      Reference entered by MMTC — editable by admin
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      value={mmtcOrderIdDraft}
                      onChange={e => setMmtcOrderIdDraft(e.target.value)}
                      placeholder="Enter MMTC order reference"
                      className="h-9 flex-1 border-slate-200 bg-white font-mono text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
                      disabled={isSavingMmtcId}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-9 shrink-0 px-4 font-bold"
                      onClick={handleSaveMmtcOrderId}
                      disabled={isSavingMmtcId || !isMmtcOrderIdDirty}
                    >
                      {isSavingMmtcId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </div>

                {(selectedOrder.customer_detail ??
                  selectedOrder.customer_name ??
                  selectedOrder.customer_email ??
                  selectedOrder.customer_phone ??
                  selectedOrder.pan_number) && (
                  <>
                    {(selectedOrder.customer_detail?.full_name ??
                      selectedOrder.customer_detail?.first_name ??
                      selectedOrder.customer_name) && (
                      <div>
                        <Label className="text-[10px] font-bold uppercase text-slate-400">
                          Name
                        </Label>
                        <p className="mt-1 font-bold text-slate-800">
                          {selectedOrder.customer_detail?.full_name ??
                            selectedOrder.customer_detail?.first_name ??
                            selectedOrder.customer_name}
                        </p>
                      </div>
                    )}
                    {(selectedOrder.customer_detail?.email ??
                      selectedOrder.customer_email) && (
                      <div>
                        <Label className="text-[10px] font-bold uppercase text-slate-400">
                          Email
                        </Label>
                        <p className="mt-1 break-all font-medium text-slate-600">
                          {selectedOrder.customer_detail?.email ??
                            selectedOrder.customer_email}
                        </p>
                      </div>
                    )}
                    {(selectedOrder.customer_detail?.phone_number ??
                      selectedOrder.customer_phone) && (
                      <div>
                        <Label className="text-[10px] font-bold uppercase text-slate-400">
                          Phone
                        </Label>
                        <p className="mt-1 font-medium text-slate-600">
                          {selectedOrder.customer_detail?.phone_number ??
                            selectedOrder.customer_phone}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-[10px] font-bold uppercase text-slate-400">
                        PAN Number
                      </Label>
                      <p className="mt-1 font-mono text-sm font-medium uppercase text-slate-600">
                        {selectedOrder.pan_number ??
                          selectedOrder.customer_detail?.pan_number ??
                          "-"}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card className="flex flex-col rounded-xl border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                <MapPin className="h-4 w-4" /> Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="mt-2 flex h-full flex-col">
                {isAddressObj ? (
                  <>
                    <p className="text-base text-sm font-bold text-slate-800">
                      {address.street_address_1 ?? address.line1}
                    </p>
                    {(address.street_address_2 ?? address.line2) && (
                      <p className="mt-1 text-sm text-slate-600">
                        {address.street_address_2 ?? address.line2}
                      </p>
                    )}
                    <p className="mt-2 text-sm text-slate-600">
                      {address.city}, {address.state}
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-800">
                      {address.pincode}
                    </p>
                    <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      {address.country}
                    </p>
                  </>
                ) : (
                  <p className="font-mono text-xs text-slate-600">
                    ID: {address ?? "No address provided"}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Information */}
          <Card className="flex flex-col rounded-xl border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                <CreditCard className="h-4 w-4" /> Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 space-y-5 pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="col-span-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Transaction ID
                  </Label>
                  <p className="mt-1 w-fit rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-xs font-bold text-slate-700">
                    {selectedOrder.transaction_id ?? "-"}
                  </p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Payment Method
                  </Label>
                  <p className="mt-1 font-bold text-slate-700">
                    {selectedOrder.payment_method ?? "-"}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Status
                  </Label>
                  <Badge
                    variant="outline"
                    className={`mt-1 w-fit px-3 py-0.5 text-[10px] font-bold uppercase ${selectedOrder.payment_status?.toUpperCase() === "SUCCESS" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-orange-200 bg-orange-50 text-orange-700"}`}
                  >
                    {selectedOrder.payment_status ?? "PENDING"}
                  </Badge>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Settlement
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`px-3 py-0.5 text-[9px] font-bold ${normalizedStatusStr.includes("DELIVER") ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                    >
                      {normalizedStatusStr.includes("DELIVER")
                        ? "DONE"
                        : "PENDING"}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] font-bold uppercase text-slate-400">
                    Order Date
                  </Label>
                  <p className="mt-1 font-bold text-slate-700">
                    {selectedOrder.order_date
                      ? new Date(selectedOrder.order_date).toLocaleString()
                      : "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Product Line Items */}
        <Card className="overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
              <Package className="h-4 w-4" /> Order Items (
              {selectedOrder.items?.length ?? 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="h-12 text-[10px] font-bold uppercase text-slate-500">
                      Product
                    </TableHead>
                    <TableHead className="h-12 text-[10px] font-bold uppercase text-slate-500">
                      SKU
                    </TableHead>
                    <TableHead className="h-12 text-[10px] font-bold uppercase text-slate-500">
                      Purity
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      Weight
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      Qty
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      Unit Price
                    </TableHead>
                    <TableHead className="h-12 text-right text-[10px] font-bold uppercase text-slate-500">
                      GST
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(selectedOrder.items ?? []).map((item: any, idx: number) => (
                    <TableRow
                      key={item.sku ?? idx}
                      className="border-slate-100 hover:bg-slate-50/50"
                    >
                      <TableCell className="py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                            {item.product_image ? (
                              <img
                                src={item.product_image}
                                alt={item.product_name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-slate-50 text-[10px] text-slate-400">
                                IMG
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-medium text-slate-800">
                            {item.product_name ?? "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 font-mono text-xs font-medium text-slate-500">
                        {item.sku ?? "-"}
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge
                          variant="outline"
                          className="border-amber-200 bg-amber-50 text-[10px] font-bold text-amber-700"
                        >
                          {item.purity ?? "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-amber-700">
                        {item.weight_grams ?? item.weight ?? "0"}g
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-slate-700">
                        {item.quantity ?? 1}
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-slate-700">
                        ₹{parseFloat(item.unit_price ?? "0").toLocaleString()}
                      </TableCell>
                      <TableCell className="py-4 text-right font-bold text-slate-700">
                        ₹
                        {(
                          parseFloat(item.gst_amount ?? "0") ||
                          parseFloat(item.line_total ?? "0") * 0.03
                        ).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Invoice-style Summary Footer */}
            <div className="border-t-2 border-slate-200 bg-slate-50/80">
              <div className="ml-auto flex w-full max-w-sm flex-col gap-0 px-6 py-4">
                <div className="flex items-center justify-between border-b border-dashed border-slate-200 py-2.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    Subtotal
                  </span>
                  <span className="text-sm font-bold text-slate-700">
                    ₹
                    {parseFloat(selectedOrder.subtotal ?? "0").toLocaleString(
                      "en-IN"
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-dashed border-slate-200 py-2.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    GST ({selectedOrder.gst_percentage ?? "3"}%)
                  </span>
                  <span className="text-sm font-bold text-slate-700">
                    ₹
                    {parseFloat(selectedOrder.gst_amount ?? "0").toLocaleString(
                      "en-IN"
                    )}
                  </span>
                </div>
                {parseFloat(selectedOrder.commission_fin_ease ?? "0") > 0 && (
                  <div className="flex items-center justify-between border-b border-dashed border-slate-200 py-2.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Commission
                    </span>
                    <span className="text-sm font-bold text-slate-700">
                      ₹
                      {parseFloat(
                        selectedOrder.commission_fin_ease ?? "0"
                      ).toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-3">
                  <span className="text-sm font-black uppercase tracking-wider text-slate-900">
                    Grand Total
                  </span>
                  <span className="text-lg font-black text-emerald-700">
                    ₹
                    {parseFloat(
                      selectedOrder.grand_total ?? "0"
                    ).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Price Breakdown */}
          <Card className="overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm md:col-span-2">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                <IndianRupee className="h-4 w-4" /> Price Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4 rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 p-6">
                <div className="grid grid-cols-2 gap-y-3 text-sm">
                  <span className="font-medium text-slate-600">
                    Item Price total
                  </span>
                  <span className="text-right font-bold text-slate-800">
                    ₹
                    {parseFloat(selectedOrder.subtotal ?? "0").toLocaleString()}
                  </span>

                  <span className="font-medium text-slate-600">GST</span>
                  <span className="text-right font-bold text-slate-800">
                    ₹
                    {parseFloat(
                      selectedOrder.gst_amount ?? "0"
                    ).toLocaleString()}
                  </span>

                  {parseFloat(selectedOrder.commission_fin_ease ?? "0") > 0 && (
                    <>
                      <span className="font-medium text-slate-600">
                        FinEase Commission
                      </span>
                      <span className="text-right font-bold text-slate-800">
                        ₹
                        {parseFloat(
                          selectedOrder.commission_fin_ease ?? "0"
                        ).toLocaleString()}
                      </span>
                    </>
                  )}

                  <div className="col-span-2 my-2 border-t border-emerald-200/60" />

                  <span className="text-base font-bold text-slate-900">
                    Total Amount
                  </span>
                  <span className="text-right text-lg font-black text-emerald-700">
                    ₹
                    {parseFloat(
                      selectedOrder.grand_total ?? "0"
                    ).toLocaleString()}
                  </span>
                </div>

                {normalizedStatusStr.includes("DELIVER") && (
                  <div className="border-t border-emerald-200/60 pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                        Settlement
                      </span>
                      <Badge className="border-amber-200 bg-amber-100 px-3 text-[10px] font-bold uppercase text-amber-700 hover:bg-amber-200">
                        PENDING
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Logistics Control */}
          <Card className="flex flex-col rounded-xl border-slate-200 bg-white shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                  <Truck className="h-4 w-4" /> Order Status
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-6">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-[10px] font-bold uppercase text-slate-500">
                  Admin Controls
                </Label>
                <Badge
                  variant="outline"
                  className="border-slate-200 bg-slate-50 text-[9px] font-bold uppercase text-slate-500"
                >
                  MANUAL OVERRIDE
                </Badge>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-slate-700">
                    Order Status
                  </Label>
                  <Select
                    value={shippingForm.status}
                    onValueChange={val =>
                      setShippingForm({ ...shippingForm, status: val })
                    }
                  >
                    <SelectTrigger className="h-10 border-slate-200 bg-white text-sm font-medium focus:ring-slate-200">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_order">New Order</SelectItem>
                      <SelectItem value="order_confirmed">Confirmed</SelectItem>
                      <SelectItem value="order_processing">
                        Processing
                      </SelectItem>
                      <SelectItem value="order_dispatched">
                        Dispatched
                      </SelectItem>
                      <SelectItem value="on_the_way">On The Way</SelectItem>
                      <SelectItem value="order_delivered">Delivered</SelectItem>
                      <SelectItem value="order_return">Return</SelectItem>
                      <SelectItem value="order_cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 border-t border-slate-100 pt-4">
                  <Label className="text-xs font-bold text-slate-700">
                    Courier Partner
                  </Label>
                  <Select
                    value={shippingForm.courier}
                    onValueChange={val =>
                      setShippingForm({ ...shippingForm, courier: val })
                    }
                  >
                    <SelectTrigger className="h-10 border-slate-200 bg-white text-sm font-medium focus:ring-slate-200">
                      <SelectValue placeholder="Select Courier Partner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BVC">BVC</SelectItem>
                      <SelectItem value="Blue Dart">Blue Dart</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold text-slate-700">
                    Tracking Number
                  </Label>
                  <Input
                    placeholder="e.g. BD123456789IN"
                    className="h-10 border-slate-200 bg-white text-sm placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus-visible:ring-slate-200"
                    value={shippingForm.tracking}
                    onChange={e =>
                      setShippingForm({
                        ...shippingForm,
                        tracking: e.target.value,
                      })
                    }
                  />
                </div>

                <Button
                  className="mt-2 h-10 w-full bg-slate-900 font-bold text-white hover:bg-slate-800"
                  onClick={() => handleUpdateStatus(shippingForm.status)}
                >
                  Update Logistics & Status
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
